import React, { useState, useEffect } from 'react';
import { Clock, Play, Trash2, RefreshCw, Calendar, Users, TrendingUp, CheckCircle, XCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import API_URL from '../config/API_URL';

const Toast = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <motion.div
      initial={{ x: 400, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 400, opacity: 0 }}
      style={{
        position: 'fixed',
        top: '2rem',
        right: '2rem',
        background: type === 'success' ? 'rgba(16, 185, 129, 0.95)' : 'rgba(239, 68, 68, 0.95)',
        backdropFilter: 'blur(10px)',
        borderRadius: '12px',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
        padding: '1rem 1.5rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        zIndex: 10000,
        border: `1px solid ${type === 'success' ? '#10b981' : '#ef4444'}`,
        minWidth: '300px',
        color: 'white',
      }}
    >
      {type === 'success' ? <CheckCircle size={24} /> : <XCircle size={24} />}
      <span style={{ fontSize: '0.95rem', fontWeight: 500 }}>{message}</span>
    </motion.div>
  );
};

const StatsCard = ({ title, value, icon: Icon, color, subtitle }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        background: 'rgba(20, 20, 35, 0.6)',
        backdropFilter: 'blur(20px)',
        borderRadius: '16px',
        padding: '1.5rem',
        border: `1px solid ${color}30`,
        boxShadow: `0 4px 30px ${color}20`,
        position: 'relative',
        overflow: 'hidden',
      }}
      whileHover={{
        scale: 1.02,
        boxShadow: `0 8px 40px ${color}30`,
      }}
    >
      <div style={{
        position: 'absolute',
        top: '-50%',
        right: '-10%',
        width: '150px',
        height: '150px',
        background: `radial-gradient(circle, ${color}15 0%, transparent 70%)`,
        borderRadius: '50%',
      }} />
      
      <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '0.75rem',
        }}>
          <span style={{
            fontSize: '0.9rem',
            color: '#a0a0c0',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '1px',
            fontFamily: "'Rajdhani', sans-serif",
          }}>
            {title}
          </span>
          <Icon size={24} style={{ color: color }} />
        </div>
        
        <div style={{
          fontSize: '2.5rem',
          fontWeight: 900,
          fontFamily: "'Orbitron', monospace",
          background: `linear-gradient(135deg, ${color}, ${color}80)`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          marginBottom: '0.5rem',
        }}>
          {value}
        </div>
        
        {subtitle && (
          <div style={{
            fontSize: '0.85rem',
            color: '#a0a0c0',
            fontWeight: 500,
          }}>
            {subtitle}
          </div>
        )}
      </div>
    </motion.div>
  );
};

const SchedulerPage = () => {
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState({ briefing_jobs: [], scheduler_jobs: [] });
  const [loading, setLoading] = useState(true);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, jobsRes] = await Promise.all([
        fetch(`${API_URL}/admin/stats`, { credentials: 'include' }),
        fetch(`${API_URL}/admin/scheduled-jobs`, { credentials: 'include' })
      ]);

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData.stats);
      }

      if (jobsRes.ok) {
        const jobsData = await jobsRes.json();
        setJobs(jobsData);
      }
    } catch (error) {
      console.error('Failed to load scheduler data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerSchedule = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/trigger-schedule`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        setToastMessage('Scheduling triggered successfully!');
        setToastType('success');
        setShowToast(true);
        setTimeout(loadData, 1000); // Reload after 1s
      } else {
        throw new Error('Failed to trigger scheduling');
      }
    } catch (error) {
      setToastMessage('Failed to trigger scheduling');
      setToastType('error');
      setShowToast(true);
    }
  };

  const handleClearJobs = async () => {
    if (!window.confirm('Are you sure you want to clear all scheduled jobs? This will not delete topics, but will remove all queued briefings.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/admin/clear-scheduled-jobs`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        setToastMessage('All scheduled jobs cleared!');
        setToastType('success');
        setShowToast(true);
        loadData();
      } else {
        throw new Error('Failed to clear jobs');
      }
    } catch (error) {
      setToastMessage('Failed to clear jobs');
      setToastType('error');
      setShowToast(true);
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return 'Not scheduled';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getTimeUntil = (isoString) => {
    if (!isoString) return null;
    const now = new Date();
    const target = new Date(isoString);
    const diff = target - now;
    
    if (diff < 0) return 'Overdue';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h`;
    }
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const styles = {
    container: {
      padding: '0',
    },
    header: {
      marginBottom: '2rem',
    },
    title: {
      fontSize: '2.5rem',
      fontWeight: 700,
      fontFamily: "'Orbitron', monospace",
      background: 'linear-gradient(135deg, #00fff5, #8b5cf6)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      marginBottom: '0.5rem',
      letterSpacing: '2px',
    },
    subtitle: {
      fontSize: '1.1rem',
      color: '#a0a0c0',
      fontFamily: "'Rajdhani', sans-serif",
      fontWeight: 500,
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1.5rem',
      marginBottom: '2rem',
    },
    controlsBar: {
      display: 'flex',
      gap: '1rem',
      marginBottom: '1.5rem',
      flexWrap: 'wrap',
    },
    button: {
      padding: '0.875rem 1.5rem',
      background: 'rgba(20, 20, 35, 0.6)',
      backdropFilter: 'blur(20px)',
      border: '1px solid rgba(0, 255, 245, 0.3)',
      borderRadius: '12px',
      color: '#00fff5',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontFamily: "'Rajdhani', sans-serif",
      letterSpacing: '0.5px',
      textTransform: 'uppercase',
    },
    section: {
      background: 'rgba(20, 20, 35, 0.6)',
      backdropFilter: 'blur(20px)',
      borderRadius: '16px',
      border: '1px solid rgba(0, 255, 245, 0.2)',
      boxShadow: '0 4px 30px rgba(0, 255, 245, 0.1)',
      padding: '1.5rem',
      marginBottom: '1.5rem',
    },
    sectionTitle: {
      fontSize: '1.5rem',
      fontWeight: 700,
      fontFamily: "'Orbitron', monospace",
      color: '#00fff5',
      marginBottom: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
    },
    jobCard: {
      background: 'rgba(30, 30, 45, 0.6)',
      borderRadius: '12px',
      padding: '1rem',
      marginBottom: '1rem',
      border: '1px solid rgba(0, 255, 245, 0.1)',
      transition: 'all 0.3s ease',
    },
    jobHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'start',
      marginBottom: '0.75rem',
    },
    jobTitle: {
      fontSize: '1.1rem',
      fontWeight: 600,
      color: '#e0e0ff',
      fontFamily: "'Rajdhani', sans-serif",
    },
    jobMeta: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '1rem',
      fontSize: '0.9rem',
      color: '#a0a0c0',
      fontFamily: "'Rajdhani', sans-serif",
    },
    badge: {
      display: 'inline-block',
      padding: '0.25rem 0.75rem',
      borderRadius: '6px',
      fontSize: '0.75rem',
      fontWeight: 700,
      fontFamily: "'Orbitron', monospace",
      letterSpacing: '1px',
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: '4rem', color: '#a0a0c0' }}>
          <RefreshCw size={48} style={{ color: '#8b5cf6', marginBottom: '1rem' }} />
          <div style={{ fontSize: '1.2rem', fontFamily: "'Rajdhani', sans-serif" }}>
            Loading scheduler data...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <AnimatePresence>
        {showToast && (
          <Toast
            message={toastMessage}
            type={toastType}
            onClose={() => setShowToast(false)}
          />
        )}
      </AnimatePresence>

      <div style={styles.header}>
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          style={styles.title}
        >
          SCHEDULER MANAGEMENT
        </motion.h1>
        <p style={styles.subtitle}>
          Monitor and manage automated briefing schedules
        </p>
      </div>

      {stats && (
        <div style={styles.statsGrid}>
          <StatsCard
            title="Total Topics"
            value={stats.total_topics}
            icon={TrendingUp}
            color="#00fff5"
            subtitle={`${stats.active_topics} active`}
          />
          <StatsCard
            title="Today's Briefings"
            value={stats.today_briefings}
            icon={Calendar}
            color="#00ff88"
            subtitle={`${stats.total_briefings} total`}
          />
          <StatsCard
            title="Daily Topics"
            value={stats.daily_topics}
            icon={Clock}
            color="#ffaa00"
            subtitle="Active schedules"
          />
          <StatsCard
            title="Users"
            value={stats.users_with_topics}
            icon={Users}
            color="#8b5cf6"
            subtitle="With topics"
          />
          <StatsCard
            title="Weekly Topics"
            value={stats.weekly_topics}
            icon={Calendar}
            color="#ff3366"
            subtitle="Active schedules"
          />
          <StatsCard
            title="Scheduled Jobs"
            value={jobs.briefing_jobs.length}
            icon={CheckCircle}
            color="#00fff5"
            subtitle="In queue"
          />
        </div>
      )}

      <div style={styles.controlsBar}>
        <motion.button
          style={styles.button}
          onClick={handleTriggerSchedule}
          whileHover={{ scale: 1.05, boxShadow: '0 0 20px rgba(0, 255, 245, 0.4)' }}
          whileTap={{ scale: 0.95 }}
        >
          <Play size={18} />
          Trigger Scheduling Now
        </motion.button>

        <motion.button
          style={styles.button}
          onClick={loadData}
          whileHover={{ scale: 1.05, boxShadow: '0 0 20px rgba(0, 255, 245, 0.4)' }}
          whileTap={{ scale: 0.95 }}
        >
          <RefreshCw size={18} />
          Refresh
        </motion.button>

        <motion.button
          style={{
            ...styles.button,
            border: '1px solid rgba(255, 51, 102, 0.3)',
            color: '#ff3366',
          }}
          onClick={handleClearJobs}
          whileHover={{ scale: 1.05, boxShadow: '0 0 20px rgba(255, 51, 102, 0.4)' }}
          whileTap={{ scale: 0.95 }}
        >
          <Trash2 size={18} />
          Clear All Jobs
        </motion.button>
      </div>

      {/* Scheduler Jobs (8 AM and 10 PM runners) */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>
          <Clock size={24} />
          Scheduler Jobs (Runners)
        </h2>
        {jobs.scheduler_jobs.map((job) => (
          <div key={job.job_id} style={styles.jobCard}>
            <div style={styles.jobHeader}>
              <div style={styles.jobTitle}>{job.name}</div>
              <span style={{
                ...styles.badge,
                background: 'rgba(0, 255, 136, 0.2)',
                color: '#00ff88',
                border: '1px solid #00ff88',
              }}>
                ACTIVE
              </span>
            </div>
            <div style={styles.jobMeta}>
              <div><Clock size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
                Next Run: {formatDateTime(job.next_run_time)}
              </div>
              <div>
                In: {getTimeUntil(job.next_run_time)}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Briefing Jobs */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>
          <Calendar size={24} />
          Scheduled Briefings ({jobs.briefing_jobs.length})
        </h2>
        {jobs.briefing_jobs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#a0a0c0' }}>
            No briefings currently scheduled. Run "Trigger Scheduling Now" to queue topics.
          </div>
        ) : (
          jobs.briefing_jobs
            .sort((a, b) => new Date(a.next_run_time) - new Date(b.next_run_time))
            .map((job) => (
              <motion.div
                key={job.job_id}
                style={styles.jobCard}
                whileHover={{ backgroundColor: 'rgba(40, 40, 55, 0.6)' }}
              >
                <div style={styles.jobHeader}>
                  <div>
                    <div style={styles.jobTitle}>{job.topic_name}</div>
                    <div style={{ fontSize: '0.85rem', color: '#8b5cf6', marginTop: '0.25rem' }}>
                      {job.user_email}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <span style={{
                      ...styles.badge,
                      background: job.frequency === 'daily' ? 'rgba(0, 255, 245, 0.2)' : 
                                 job.frequency === 'weekly' ? 'rgba(255, 170, 0, 0.2)' :
                                 'rgba(139, 92, 246, 0.2)',
                      color: job.frequency === 'daily' ? '#00fff5' :
                             job.frequency === 'weekly' ? '#ffaa00' :
                             '#8b5cf6',
                      border: `1px solid ${job.frequency === 'daily' ? '#00fff5' :
                              job.frequency === 'weekly' ? '#ffaa00' :
                              '#8b5cf6'}`,
                    }}>
                      {job.frequency.toUpperCase()}
                    </span>
                    {job.is_active && (
                      <span style={{
                        ...styles.badge,
                        background: 'rgba(0, 255, 136, 0.2)',
                        color: '#00ff88',
                        border: '1px solid #00ff88',
                      }}>
                        ACTIVE
                      </span>
                    )}
                  </div>
                </div>
                <div style={styles.jobMeta}>
                  <div><Clock size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
                    Scheduled: {formatDateTime(job.next_run_time)}
                  </div>
                  <div>
                    In: <strong style={{ color: '#00fff5' }}>{getTimeUntil(job.next_run_time)}</strong>
                  </div>
                  <div>
                    Job ID: {job.job_id}
                  </div>
                </div>
              </motion.div>
            ))
        )}
      </div>
    </div>
  );
};

export default SchedulerPage;